import type { Species } from '../schemas/data/species.js';

/*
 * Wave 2 of the 500-squishy plan (#382): 9 hand-designed kinds, found in the
 * wild by biome, time of day and light (see the server spawn tables, ids
 * `wave-2-*`). 6 common, 2 uncommon, 1 rare, picked by the owner from a brief
 * of 28; the 19 that did not read at a glance as toys were left out rather
 * than shipped weak. Each is built round one five-word hook a kid can say: a
 * penguin that slides on its tummy, a sheep made of cloud, an anglerfish
 * scared of its own light. #443 gives each line a kid and one teen, listed
 * after its kind. No wave-2 form is met as a guardian. Public data like the
 * rest of `species.ts`; where each one spawns is server-only.
 *
 * Stat budgets (hp + attack + defense + speed) follow the launch bands in
 * `species.ts` (TUNE: every `baseStats`): common 195-215, uncommon 210-240,
 * rare 225-245; a kid is ×1.18 its kind and a teen ×1.4. Every move comes
 * from the existing `MOVES`, and every part from the existing registry.
 */
export const WAVE_2_SPECIES: Species[] = [
  // Slipwaddle line (#443): baby, kid, teen.
  {
    id: 'slipwaddle',
    name: 'Slipwaddle',
    description: 'A penguin who slides everywhere on its tummy. It says whee every single time.',
    element: 'frost',
    feeling: 'joy',
    rarity: 'common',
    baseStats: { hp: 55, attack: 50, defense: 45, speed: 60 },
    moves: ['snowball-toss', 'twirly-whirl', 'chilly-yawn'],
    evolutions: [{ into: 'sledwaddle', level: 16 }], // TUNE: #443 kid at the common level,
    visual: {
      body: 'barrel',
      palette: ['#2f3f66', '#7fe6ff', '#ff9f43', '#9ad8f0'],
      parts: ['close-happy-eyes', 'beak', 'blush-cheeks', 'side-fins', 'slide-trail', 'belly-oval'],
      head: { body: 'orb', size: 0.95, forward: 0.55, up: 0.2 },
      stance: 0,
      pose: 'slither',
      attackPart: 'wings',
      ink: '#f4fbff',
      size: 0.9,
    },
    habitatPreferences: { elements: ['frost'], feelings: ['joy', 'silly'] },
  },
  {
    id: 'sledwaddle',
    name: 'Sledwaddle',
    description: 'A little ice tuft popped up on its head. It slides faster now, and spins.',
    element: 'frost',
    feeling: 'joy',
    rarity: 'common',
    baseStats: { hp: 65, attack: 59, defense: 53, speed: 71 }, // TUNE: ×1.18 Slipwaddle
    moves: ['snowball-toss', 'twirly-whirl', 'chilly-yawn'],
    evolutions: [{ into: 'blizzwaddle', level: 24 }], // TUNE: kid level + 8 (#443),
    visual: {
      body: 'barrel',
      palette: ['#2f3f66', '#7fe6ff', '#ff9f43', '#9ad8f0'],
      parts: [
        'close-happy-eyes',
        'beak',
        'blush-cheeks',
        'side-fins',
        'slide-trail',
        'belly-oval',
        'water-curl',
      ],
      head: { body: 'orb', size: 0.95, forward: 0.55, up: 0.2 },
      stance: 0,
      pose: 'slither',
      attackPart: 'wings',
      ink: '#f4fbff',
      size: 1.25,
    },
    habitatPreferences: { elements: ['frost'], feelings: ['joy', 'silly'] },
  },
  {
    id: 'blizzwaddle',
    name: 'Blizzwaddle',
    description: 'A snug scarf and strong flippers. It slides in, spins out and takes a bow.',
    element: 'frost',
    feeling: 'joy',
    rarity: 'uncommon',
    baseStats: { hp: 77, attack: 70, defense: 63, speed: 84 }, // TUNE: ×1.4 Slipwaddle
    moves: ['snowball-toss', 'twirly-whirl', 'chilly-yawn'],
    evolutions: [],
    visual: {
      body: 'barrel',
      palette: ['#2f3f66', '#7fe6ff', '#ff9f43', '#9ad8f0'],
      parts: [
        'close-happy-eyes',
        'beak',
        'blush-cheeks',
        'side-fins',
        'slide-trail',
        'belly-oval',
        'icicle-crown',
        'fin-row',
      ],
      head: { body: 'orb', size: 0.95, forward: 0.55, up: 0.2 },
      stance: 0,
      pose: 'slither',
      attackPart: 'wings',
      ink: '#f4fbff',
      size: 1.55,
    },
    habitatPreferences: { elements: ['frost'], feelings: ['joy', 'silly'] },
  },

  // Oinkle line (#443): baby, kid, teen.
  {
    id: 'oinkle',
    name: 'Oinkle',
    description: 'A piglet who adores puddle baths. Snort means yes. Two snorts means more bath.',
    element: 'stone',
    feeling: 'cozy',
    rarity: 'common',
    baseStats: { hp: 60, attack: 50, defense: 55, speed: 40 },
    moves: ['pebble-plop', 'rock-a-bye', 'sturdy-sit'],
    evolutions: [{ into: 'snortle', level: 16 }], // TUNE: #443 kid at the common level,
    visual: {
      body: 'barrel',
      palette: ['#ffb3c7', '#f08aa8', '#e0608a', '#7a4a2a'],
      parts: ['oval-eyes', 'pig-snout', 'blush-cheeks', 'pig-ears', 'stubby-legs', 'pig-tail'],
      head: { body: 'orb', size: 0.95, forward: 0.8, up: 0.3 },
      stance: 0.1,
      pose: 'stand',
      attackPart: 'tail',
      size: 0.9,
    },
    habitatPreferences: { elements: ['stone'], feelings: ['cozy', 'sleepy'] },
  },
  {
    id: 'snortle',
    name: 'Snortle',
    description: 'Its little arms are just big enough to hug a puddle. Mud on the nose is a style.',
    element: 'stone',
    feeling: 'cozy',
    rarity: 'common',
    baseStats: { hp: 71, attack: 59, defense: 65, speed: 47 }, // TUNE: ×1.18 Oinkle
    moves: ['pebble-plop', 'rock-a-bye', 'sturdy-sit'],
    evolutions: [{ into: 'tuskoink', level: 24 }], // TUNE: kid level + 8 (#443),
    visual: {
      body: 'barrel',
      palette: ['#ffb3c7', '#f08aa8', '#e0608a', '#7a4a2a'],
      parts: [
        'oval-eyes',
        'pig-snout',
        'blush-cheeks',
        'pig-ears',
        'stubby-legs',
        'pig-tail',
        'pig-bubbles',
      ],
      head: { body: 'orb', size: 0.95, forward: 0.8, up: 0.3 },
      stance: 0.1,
      pose: 'stand',
      attackPart: 'tail',
      size: 1.25,
    },
    habitatPreferences: { elements: ['stone'], feelings: ['cozy', 'sleepy'] },
  },
  {
    id: 'tuskoink',
    name: 'Tuskoink',
    description:
      'Tiny tusks and a fluffy mane. It is very proud of both, and still loves bath time.',
    element: 'stone',
    feeling: 'cozy',
    rarity: 'uncommon',
    baseStats: { hp: 84, attack: 70, defense: 77, speed: 56 }, // TUNE: ×1.4 Oinkle
    moves: ['pebble-plop', 'rock-a-bye', 'sturdy-sit'],
    evolutions: [],
    visual: {
      body: 'barrel',
      palette: ['#ffb3c7', '#f08aa8', '#e0608a', '#7a4a2a'],
      parts: [
        'close-oval-eyes',
        'pig-snout',
        'blush-cheeks',
        'pig-ears',
        'stubby-legs',
        'pig-tail',
        'pig-bubbles',
        'pig-tusks',
        'back-spines',
      ],
      head: { body: 'orb', size: 0.95, forward: 0.8, up: 0.3 },
      stance: 0.1,
      pose: 'stand',
      attackPart: 'tail',
      size: 1.55,
      ink: '#3b2a3f',
    },
    habitatPreferences: { elements: ['stone'], feelings: ['cozy', 'sleepy'] },
  },

  // Cubbun line (#443): baby, kid, teen.
  {
    id: 'cubbun',
    name: 'Cubbun',
    description: 'A bear cub who hugs a big red heart whenever it is happy. Both arms, every time.',
    element: 'leaf',
    feeling: 'cozy',
    rarity: 'common',
    baseStats: { hp: 65, attack: 50, defense: 55, speed: 40 },
    moves: ['leafy-tickle', 'mossy-nap', 'pollen-puff'],
    evolutions: [{ into: 'brambun', level: 16 }], // TUNE: #443 kid at the common level,
    visual: {
      body: 'pear',
      palette: ['#c89a6c', '#7fcf5a', '#b5532a', '#d63a4e'],
      parts: [
        'oval-eyes',
        'tiny-smile',
        'blush-cheeks',
        'round-ears',
        'heart-arms',
        'heart-lobes',
        'heart-point',
        'heart-paws',
        'stubby-legs',
      ],
      head: { body: 'orb', size: 0.9, forward: 0.15, up: 0.5 },
      stance: 0.05,
      pose: 'sit',
      attackPart: 'arms',
      size: 0.9,
    },
    habitatPreferences: { elements: ['leaf'], feelings: ['cozy', 'sleepy'] },
  },
  {
    id: 'brambun',
    name: 'Brambun',
    description:
      'A green sprout on its head and an even bigger self-hug. It hugs friends too, if they hold still.',
    element: 'leaf',
    feeling: 'cozy',
    rarity: 'common',
    baseStats: { hp: 77, attack: 59, defense: 65, speed: 47 }, // TUNE: ×1.18 Cubbun
    moves: ['leafy-tickle', 'mossy-nap', 'pollen-puff'],
    evolutions: [{ into: 'grizzbun', level: 24 }], // TUNE: kid level + 8 (#443),
    visual: {
      body: 'pear',
      palette: ['#c89a6c', '#7fcf5a', '#b5532a', '#d63a4e'],
      parts: [
        'oval-eyes',
        'tiny-smile',
        'blush-cheeks',
        'round-ears',
        'heart-arms',
        'heart-lobes',
        'heart-point',
        'heart-paws',
        'stubby-legs',
        'leaf-sprout',
      ],
      head: { body: 'orb', size: 0.9, forward: 0.15, up: 0.5 },
      stance: 0.05,
      pose: 'sit',
      attackPart: 'arms',
      size: 1.2,
    },
    habitatPreferences: { elements: ['leaf'], feelings: ['cozy', 'sleepy'] },
  },
  {
    id: 'grizzbun',
    name: 'Grizzbun',
    description: 'Strong arms, a leafy back and the biggest hug in the woods. Please hug back.',
    element: 'leaf',
    feeling: 'cozy',
    rarity: 'uncommon',
    baseStats: { hp: 91, attack: 70, defense: 77, speed: 56 }, // TUNE: ×1.4 Cubbun
    moves: ['leafy-tickle', 'mossy-nap', 'pollen-puff'],
    evolutions: [],
    visual: {
      body: 'pear',
      palette: ['#c89a6c', '#7fcf5a', '#b5532a', '#d63a4e'],
      parts: [
        'oval-eyes',
        'tiny-smile',
        'blush-cheeks',
        'round-ears',
        'heart-arms',
        'heart-lobes',
        'heart-point',
        'heart-paws',
        'stubby-legs',
        'leaf-sprout',
        'leaf-crown',
        'brave-brows',
      ],
      head: { body: 'orb', size: 0.9, forward: 0.15, up: 0.5 },
      stance: 0.05,
      pose: 'sit',
      attackPart: 'arms',
      size: 1.65,
    },
    habitatPreferences: { elements: ['leaf'], feelings: ['cozy', 'sleepy'] },
  },

  // Spudsy line (#443): baby, kid, teen.
  {
    id: 'spudsy',
    name: 'Spudsy',
    description: 'A potato with big dreams and one very small sprout. Cozy, always.',
    element: 'stone',
    feeling: 'cozy',
    rarity: 'common',
    baseStats: { hp: 65, attack: 45, defense: 60, speed: 35 },
    moves: ['pebble-plop', 'rock-a-bye', 'sturdy-sit'],
    evolutions: [{ into: 'tatertot', level: 16 }], // TUNE: #443 kid at the common level,
    visual: {
      body: 'pear',
      palette: ['#c9a06b', '#5fcf4a', '#4fbf3a', '#8a6238'],
      parts: [
        'oval-eyes',
        'smile',
        'blush-cheeks',
        'freckles',
        'crag-nubs',
        'stubby-legs',
        'stem',
        'spud-arms',
      ],
      stance: 0.05,
      pose: 'stand',
      attackPart: 'crown',
    },
    habitatPreferences: { elements: ['stone'], feelings: ['cozy', 'sleepy'] },
  },
  {
    id: 'tatertot',
    name: 'Tatertot',
    description: 'It grew little arms, mostly for hugging the couch. The sprout is thriving.',
    element: 'stone',
    feeling: 'cozy',
    rarity: 'common',
    baseStats: { hp: 77, attack: 53, defense: 71, speed: 41 }, // TUNE: ×1.18 Spudsy
    moves: ['pebble-plop', 'rock-a-bye', 'sturdy-sit'],
    evolutions: [{ into: 'hashbrawn', level: 24 }], // TUNE: kid level + 8 (#443),
    visual: {
      body: 'tall',
      palette: ['#c9a06b', '#5fcf4a', '#4fbf3a', '#8a6238'],
      parts: [
        'oval-eyes',
        'smile',
        'blush-cheeks',
        'freckles',
        'crag-nubs',
        'stubby-legs',
        'leaf-sprout',
        'spud-arms',
      ],
      stance: 0.05,
      pose: 'stand',
      attackPart: 'crown',
      size: 1.3,
    },
    habitatPreferences: { elements: ['stone'], feelings: ['cozy', 'sleepy'] },
  },
  {
    id: 'hashbrawn',
    name: 'Hashbrawn',
    description: 'Big arms and big feet. The strongest potato in the patch, and very gentle.',
    element: 'stone',
    feeling: 'cozy',
    rarity: 'uncommon',
    baseStats: { hp: 91, attack: 63, defense: 84, speed: 49 }, // TUNE: ×1.4 Spudsy
    moves: ['pebble-plop', 'rock-a-bye', 'sturdy-sit'],
    evolutions: [],
    visual: {
      body: 'pear',
      palette: ['#c9a06b', '#5fcf4a', '#4fbf3a', '#8a6238'],
      parts: [
        'oval-eyes',
        'smile',
        'blush-cheeks',
        'freckles',
        'crag-nubs',
        'stubby-legs',
        'leaf-sprout',
        'spud-strong-arms',
        'thorn-mantle',
      ],
      stance: 0.05,
      pose: 'stand',
      attackPart: 'crown',
      size: 1.55,
    },
    habitatPreferences: { elements: ['stone'], feelings: ['cozy', 'sleepy'] },
  },

  // Cloudlamb line (#443): baby, kid, teen.
  {
    id: 'cloudlamb',
    name: 'Cloudlamb',
    description: 'A sheep who is secretly a cloud. It drizzles a tiny bit when it yawns.',
    element: 'light',
    feeling: 'sleepy',
    rarity: 'common',
    baseStats: { hp: 60, attack: 40, defense: 50, speed: 45 },
    moves: ['sunny-beam', 'glow-up', 'moonbeam-lullaby'],
    evolutions: [{ into: 'cumulamb', level: 16 }], // TUNE: #443 kid at the common level,
    visual: {
      body: 'mochi',
      palette: ['#b5c6f2', '#c9d8ff', '#ffd89a', '#f4f7ff'],
      parts: [
        'sleepy-eyes',
        'tiny-smile',
        'cloud-crown',
        'tuft-ears',
        'fur-ruff',
        'fluff-tail',
        'crag-nubs',
      ],
      stance: 0.45,
      pose: 'hover',
      glow: 'body',
      attackPart: 'crown',
      size: 0.9,
    },
    habitatPreferences: { elements: ['light'], feelings: ['sleepy', 'cozy'] },
  },
  {
    id: 'cumulamb',
    name: 'Cumulamb',
    description: 'A fluffy lilac collar to snuggle into. It bumps other clouds hello, very gently.',
    element: 'light',
    feeling: 'sleepy',
    rarity: 'common',
    baseStats: { hp: 71, attack: 47, defense: 59, speed: 53 }, // TUNE: ×1.18 Cloudlamb
    moves: ['sunny-beam', 'glow-up', 'moonbeam-lullaby'],
    evolutions: [{ into: 'fluffasus', level: 24 }], // TUNE: kid level + 8 (#443),
    visual: {
      body: 'mochi',
      palette: ['#b5c6f2', '#c9d8ff', '#ffd89a', '#f4f7ff'],
      parts: [
        'sleepy-eyes',
        'tiny-smile',
        'cloud-crown',
        'tuft-ears',
        'fur-ruff',
        'fluff-tail',
        'crag-nubs',
        'ram-horns',
      ],
      stance: 0.45,
      pose: 'hover',
      glow: 'body',
      attackPart: 'crown',
      size: 1.25,
    },
    habitatPreferences: { elements: ['light'], feelings: ['sleepy', 'cozy'] },
  },
  {
    id: 'fluffasus',
    name: 'Fluffasus',
    description:
      'Soft little wings sprouted from the cloud. It glides low and does not leave the fluff behind.',
    element: 'light',
    feeling: 'sleepy',
    rarity: 'uncommon',
    baseStats: { hp: 84, attack: 56, defense: 70, speed: 63 }, // TUNE: ×1.4 Cloudlamb
    moves: ['sunny-beam', 'glow-up', 'moonbeam-lullaby'],
    evolutions: [],
    visual: {
      body: 'mochi',
      palette: ['#b5c6f2', '#c9d8ff', '#ffd89a', '#f4f7ff'],
      parts: [
        'sleepy-eyes',
        'tiny-smile',
        'cloud-crown',
        'tuft-ears',
        'fur-ruff',
        'fluff-tail',
        'ram-horns',
        'puff-wings',
        'wing-lobe-mid',
        'wing-lobe-low',
      ],
      stance: 0.45,
      pose: 'hover',
      glow: 'body',
      attackPart: 'crown',
      size: 1.55,
    },
    habitatPreferences: { elements: ['light'], feelings: ['sleepy', 'cozy'] },
  },

  // Glimbug line (#443): baby, kid, teen.
  {
    id: 'glimbug',
    name: 'Glimbug',
    description: 'A firefly who lights the way home. It would rather not be alone in the dark.',
    element: 'light',
    feeling: 'cozy',
    rarity: 'uncommon',
    baseStats: { hp: 55, attack: 55, defense: 50, speed: 60 },
    moves: ['sunny-beam', 'night-light', 'night-light-hug'],
    evolutions: [{ into: 'glimmerbug', level: 18 }], // TUNE: #443 kid at the uncommon level,
    visual: {
      body: 'pear',
      palette: ['#34284c', '#ffe27a', '#fff6c0', '#a8864a'],
      parts: [
        'close-oval-eyes',
        'glim-smile',
        'blush-cheeks',
        'zing-antennae',
        'glim-wings',
        'glim-lobe',
      ],
      head: { body: 'orb', size: 0.62, forward: 0.1, up: 0.75 },
      stance: 0.2,
      pose: 'hover',
      glow: 'body',
      attackPart: 'wings',
      ink: '#fff1a8',
      size: 0.9,
    },
    habitatPreferences: { elements: ['light'], feelings: ['cozy', 'sleepy'] },
  },
  {
    id: 'glimmerbug',
    name: 'Glimmerbug',
    description: 'A tiny crest lit up on its head. It is like a lantern for friends.',
    element: 'light',
    feeling: 'cozy',
    rarity: 'uncommon',
    baseStats: { hp: 65, attack: 65, defense: 59, speed: 71 }, // TUNE: ×1.18 Glimbug
    moves: ['sunny-beam', 'night-light', 'night-light-hug'],
    evolutions: [{ into: 'beaconfly', level: 26 }], // TUNE: kid level + 8 (#443),
    visual: {
      body: 'pear',
      palette: ['#34284c', '#ffe27a', '#fff6c0', '#a8864a'],
      parts: [
        'close-oval-eyes',
        'glim-smile',
        'blush-cheeks',
        'zing-antennae',
        'glim-wings',
        'glim-lobe',
        'sun-crown',
      ],
      head: { body: 'orb', size: 0.62, forward: 0.1, up: 0.75 },
      stance: 0.2,
      pose: 'hover',
      glow: 'body',
      attackPart: 'wings',
      ink: '#fff1a8',
      size: 1.25,
    },
    habitatPreferences: { elements: ['light'], feelings: ['cozy', 'sleepy'] },
  },
  {
    id: 'beaconfly',
    name: 'Beaconfly',
    description: 'A ring of light glows around its head. Follow it home. It will wait for you.',
    element: 'light',
    feeling: 'cozy',
    rarity: 'rare',
    baseStats: { hp: 77, attack: 77, defense: 70, speed: 84 }, // TUNE: ×1.4 Glimbug
    moves: ['sunny-beam', 'night-light', 'night-light-hug'],
    evolutions: [],
    visual: {
      body: 'pear',
      palette: ['#34284c', '#ffe27a', '#fff6c0', '#a8864a'],
      parts: [
        'close-oval-eyes',
        'glim-smile',
        'blush-cheeks',
        'zing-antennae',
        'glim-wings-wide',
        'glim-lobe',
        'sun-crown',
        'spark-crest',
        'glim-hands',
      ],
      head: { body: 'orb', size: 0.62, forward: 0.1, up: 0.75 },
      stance: 0.2,
      pose: 'hover',
      glow: 'body',
      attackPart: 'wings',
      ink: '#fff1a8',
      size: 1.45,
    },
    habitatPreferences: { elements: ['light'], feelings: ['cozy', 'sleepy'] },
  },

  // Dragofruit line (#443): baby, kid, teen.
  {
    id: 'dragofruit',
    name: 'Dragofruit',
    description: 'A dragon who is also a dragonfruit. It puffs sweet pink sparkles.',
    element: 'leaf',
    feeling: 'joy',
    rarity: 'uncommon',
    baseStats: { hp: 55, attack: 65, defense: 55, speed: 55 },
    moves: ['leafy-tickle', 'prickle-roll', 'sprinkle-sneeze', 'petal-party'],
    evolutions: [{ into: 'drakeberry', level: 18 }], // TUNE: #443 kid at the uncommon level,
    visual: {
      body: 'orb',
      palette: ['#ff75af', '#ffd0e4', '#6fcf5a', '#2b2430'],
      parts: [
        'happy-eyes',
        'smile',
        'blush-cheeks',
        'cone-horns',
        'back-scales',
        'nub-wings',
        'spike-tail',
      ],
      stance: 0,
      pose: 'sit',
      attackPart: 'tail',
    },
    habitatPreferences: { elements: ['leaf'], feelings: ['joy', 'brave'] },
  },
  {
    id: 'drakeberry',
    name: 'Drakeberry',
    description: 'Green spines grew down its back. They are leafy, not scary. Mostly.',
    element: 'leaf',
    feeling: 'joy',
    rarity: 'uncommon',
    baseStats: { hp: 65, attack: 77, defense: 65, speed: 65 }, // TUNE: ×1.18 Dragofruit
    moves: ['leafy-tickle', 'prickle-roll', 'sprinkle-sneeze', 'petal-party'],
    evolutions: [{ into: 'dragonbloom', level: 26 }], // TUNE: kid level + 8 (#443),
    visual: {
      body: 'orb',
      palette: ['#ff75af', '#ffd0e4', '#6fcf5a', '#2b2430'],
      parts: [
        'happy-eyes',
        'smile',
        'blush-cheeks',
        'cone-horns',
        'back-scales',
        'nub-wings',
        'spike-tail',
        'back-spines',
      ],
      size: 1.1,
      stance: 0,
      pose: 'sit',
      attackPart: 'tail',
    },
    habitatPreferences: { elements: ['leaf'], feelings: ['joy', 'brave'] },
  },
  {
    id: 'dragonbloom',
    name: 'Dragonbloom',
    description: 'Big leafy wings and a bold smirk. Part dragon, part fruit, all show-off.',
    element: 'leaf',
    feeling: 'joy',
    rarity: 'rare',
    baseStats: { hp: 77, attack: 91, defense: 77, speed: 77 }, // TUNE: ×1.4 Dragofruit
    moves: ['leafy-tickle', 'prickle-roll', 'sprinkle-sneeze', 'petal-party'],
    evolutions: [],
    visual: {
      body: 'orb',
      palette: ['#ff75af', '#ffd0e4', '#6fcf5a', '#2b2430'],
      parts: [
        'happy-eyes',
        'smirk',
        'blush-cheeks',
        'cone-horns',
        'back-scales',
        'big-wings',
        'spike-tail',
        'back-spines',
      ],
      size: 1.26,
      stance: 0,
      pose: 'sit',
      attackPart: 'tail',
    },
    habitatPreferences: { elements: ['leaf'], feelings: ['joy', 'brave'] },
  },

  // Quackle line (#443): baby, kid, teen. Swapped in for Shinybeet, which never read as a beetle.
  {
    id: 'quackle',
    name: 'Quackle',
    description:
      'A rubber duck that blows bubbles when it is happy. Squeak means yes. Bubbles mean very yes.',
    element: 'water',
    feeling: 'joy',
    rarity: 'common',
    baseStats: { hp: 60, attack: 50, defense: 50, speed: 45 },
    moves: ['giggle-drizzle', 'bubble-bath', 'belly-flop'],
    evolutions: [{ into: 'bubbleduck', level: 16 }],
    visual: {
      body: 'mochi',
      palette: ['#ffd83a', '#bfe9ff', '#ff8a1f', '#ffe9a0'],
      parts: ['happy-eyes', 'duck-bill', 'blush-cheeks', 'duck-tail', 'bubble-stream'],
      head: { body: 'orb', size: 0.8, forward: 0.5, up: 0.45 },
      stance: 0,
      pose: 'stand',
      attackPart: 'horns',
    },
    habitatPreferences: { elements: ['water'], feelings: ['joy', 'silly'] },
  },
  {
    id: 'bubbleduck',
    name: 'Bubbleduck',
    description:
      'Its bubbles got bigger and its tail got wigglier. It splashes in every puddle on purpose.',
    element: 'water',
    feeling: 'joy',
    rarity: 'common',
    baseStats: { hp: 71, attack: 59, defense: 59, speed: 53 }, // TUNE: ×1.18 Quackle
    moves: ['giggle-drizzle', 'bubble-bath', 'belly-flop'],
    evolutions: [{ into: 'soapduke', level: 24 }],
    visual: {
      body: 'mochi',
      palette: ['#ffd83a', '#bfe9ff', '#ff8a1f', '#ffe9a0'],
      parts: [
        'happy-eyes',
        'duck-bill',
        'blush-cheeks',
        'duck-tail',
        'bubble-stream-long',
        'bubble-sail',
      ],
      head: { body: 'orb', size: 0.8, forward: 0.5, up: 0.45 },
      stance: 0,
      pose: 'stand',
      attackPart: 'horns',
      size: 1.3,
    },
    habitatPreferences: { elements: ['water'], feelings: ['joy', 'silly'] },
  },
  {
    id: 'soapduke',
    name: 'Soapduke',
    description:
      'A fluffy suds collar and a very proud quack. It rules the bath, and the bath agrees.',
    element: 'water',
    feeling: 'joy',
    rarity: 'uncommon',
    baseStats: { hp: 84, attack: 70, defense: 70, speed: 63 }, // TUNE: ×1.4 Quackle
    moves: ['giggle-drizzle', 'bubble-bath', 'belly-flop'],
    evolutions: [],
    visual: {
      body: 'mochi',
      palette: ['#ffd83a', '#bfe9ff', '#ff8a1f', '#ffe9a0'],
      parts: [
        'happy-eyes',
        'duck-bill',
        'blush-cheeks',
        'duck-tail',
        'bubble-stream-tall',
        'bubble-sail',
        'suds-ruff',
      ],
      head: { body: 'orb', size: 0.8, forward: 0.55, up: 0.72 },
      stance: 0,
      pose: 'stand',
      attackPart: 'horns',
      size: 1.65,
    },
    habitatPreferences: { elements: ['water'], feelings: ['joy', 'silly'] },
  },

  // Lampfin line (#443): baby, kid, teen.
  {
    id: 'lampfin',
    name: 'Lampfin',
    description: 'An anglerfish who is scared of its own light. It blinks it on and off.',
    element: 'water',
    feeling: 'spooky',
    rarity: 'rare',
    baseStats: { hp: 65, attack: 60, defense: 55, speed: 55 },
    moves: ['giggle-drizzle', 'splish-splash', 'boo', 'peekaboo'],
    evolutions: [{ into: 'lanternfin', level: 22 }], // TUNE: #443 kid at the rare level,
    visual: {
      body: 'blob',
      palette: ['#2b5c86', '#8fd3ff', '#fffbd0', '#183550'],
      parts: [
        'close-spooky-eyes',
        'boo-mouth',
        'brave-brows',
        'lure-beads',
        'side-fins',
        'tail-fan',
      ],
      stance: 0,
      pose: 'hover',
      glow: 'accent',
      attackPart: 'tail',
      ink: '#ffe03a',
      size: 0.9,
    },
    habitatPreferences: { elements: ['water'], feelings: ['spooky', 'sleepy'] },
  },
  {
    id: 'lanternfin',
    name: 'Lanternfin',
    description: 'A tiny fin row grew along its back. It still jumps when its light turns on.',
    element: 'water',
    feeling: 'spooky',
    rarity: 'rare',
    baseStats: { hp: 77, attack: 71, defense: 65, speed: 65 }, // TUNE: ×1.18 Lampfin
    moves: ['giggle-drizzle', 'splish-splash', 'boo', 'peekaboo'],
    evolutions: [{ into: 'nightfin', level: 30 }], // TUNE: kid level + 8 (#443),
    visual: {
      body: 'blob',
      palette: ['#2b5c86', '#8fd3ff', '#fffbd0', '#183550'],
      parts: [
        'close-spooky-eyes',
        'boo-mouth',
        'brave-brows',
        'lure-beads',
        'side-fins',
        'tail-fan',
        'fin-row',
      ],
      stance: 0,
      pose: 'hover',
      glow: 'accent',
      attackPart: 'tail',
      ink: '#ffe03a',
      size: 1.25,
    },
    habitatPreferences: { elements: ['water'], feelings: ['spooky', 'sleepy'] },
  },
  {
    id: 'nightfin',
    name: 'Nightfin',
    description:
      'Big gliding fins and a frilly collar. It lights up the dark lake. Not scared. A little.',
    element: 'water',
    feeling: 'spooky',
    rarity: 'epic',
    baseStats: { hp: 91, attack: 84, defense: 77, speed: 77 }, // TUNE: ×1.4 Lampfin
    moves: ['giggle-drizzle', 'splish-splash', 'boo', 'peekaboo'],
    evolutions: [],
    visual: {
      body: 'blob',
      palette: ['#2b5c86', '#8fd3ff', '#fffbd0', '#183550'],
      parts: [
        'close-spooky-eyes',
        'boo-mouth',
        'brave-brows',
        'lure-beads',
        'side-fins',
        'tail-fan',
        'fin-row',
        'icicle-fins',
      ],
      stance: 0,
      pose: 'hover',
      glow: 'accent',
      attackPart: 'tail',
      ink: '#ffe03a',
      size: 1.55,
      finish: 'sparkle',
    },
    habitatPreferences: { elements: ['water'], feelings: ['spooky', 'sleepy'] },
  },
];
